"use client"

import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import {
  CheckIcon,
  CopyIcon,
  HouseIcon,
  RotateCwIcon,
  TriangleAlertIcon,
  UtensilsCrossedIcon,
} from "lucide-react"

import { reportClientError } from "@/lib/report-client-error"

/**
 * Last-resort error boundary (US-011, hole B). Catches errors thrown by the root
 * layout itself, so it REPLACES the root layout and must render its own
 * <html>/<body>. Because it sits above every provider (next-intl, next-themes,
 * tenant theme), it can't use translation hooks or the tenant's CSS vars — so
 * copy, colors and layout are self-contained and inline-styled, and render even
 * if global CSS failed to load. Never blank.
 *
 * Copy mirrors `error.*` / `notFound.home` in `messages/*.json`, picked from the
 * URL's `[locale]` segment (the routing locale is always the first segment).
 */
const COPY = {
  "en-GB": {
    title: "Something went wrong",
    description: "We couldn't load this page. Please try again.",
    retry: "Try again",
    home: "Back to home",
    reference: "Reference",
    copy: "Copy reference",
    copied: "Copied",
  },
  fa: {
    title: "مشکلی پیش آمد",
    description: "بارگیری این صفحه ممکن نشد. لطفاً دوباره تلاش کنید.",
    retry: "تلاش دوباره",
    home: "بازگشت به خانه",
    reference: "کد پیگیری",
    copy: "کپی کد پیگیری",
    copied: "کپی شد",
  },
  tr: {
    title: "Bir şeyler ters gitti",
    description: "Bu sayfayı yükleyemedik. Lütfen tekrar deneyin.",
    retry: "Tekrar dene",
    home: "Ana sayfaya dön",
    reference: "Referans",
    copy: "Referansı kopyala",
    copied: "Kopyalandı",
  },
} as const

type Locale = keyof typeof COPY

const RTL_LOCALES: readonly Locale[] = ["fa"]

function localeFromPath(pathname: string | null): Locale {
  const segment = pathname?.split("/")[1] ?? ""
  return segment in COPY ? (segment as Locale) : "en-GB"
}

/**
 * Self-contained styles: neutral warm palette as CSS vars, light by default,
 * dark under the OS preference unless the visitor's saved next-themes choice
 * (`data-theme`, set from localStorage below) says otherwise. Hover/focus use
 * transitions only — no keyframe animation (project rule: GSAP for motion).
 */
const STYLES = `
:root {
  --ge-bg: #faf7f2;
  --ge-glow-a: rgba(234, 88, 12, 0.16);
  --ge-glow-b: rgba(244, 63, 94, 0.10);
  --ge-card: rgba(255, 255, 255, 0.86);
  --ge-border: rgba(28, 25, 23, 0.08);
  --ge-text: #1c1917;
  --ge-muted: #57534e;
  --ge-subtle: #a8a29e;
  --ge-accent: #ea580c;
  --ge-accent-soft: rgba(234, 88, 12, 0.12);
  --ge-accent-text: #ffffff;
  --ge-warn: #e11d48;
  --ge-chip: rgba(28, 25, 23, 0.05);
  --ge-shadow: 0 24px 60px -24px rgba(28, 25, 23, 0.25);
  color-scheme: light;
}
@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --ge-bg: #0c0a09;
    --ge-glow-a: rgba(234, 88, 12, 0.22);
    --ge-glow-b: rgba(244, 63, 94, 0.12);
    --ge-card: rgba(28, 25, 23, 0.82);
    --ge-border: rgba(250, 250, 249, 0.08);
    --ge-text: #fafaf9;
    --ge-muted: #a8a29e;
    --ge-subtle: #78716c;
    --ge-accent: #f97316;
    --ge-accent-soft: rgba(249, 115, 22, 0.16);
    --ge-warn: #fb7185;
    --ge-chip: rgba(250, 250, 249, 0.06);
    --ge-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.7);
    color-scheme: dark;
  }
}
:root[data-theme="dark"] {
  --ge-bg: #0c0a09;
  --ge-glow-a: rgba(234, 88, 12, 0.22);
  --ge-glow-b: rgba(244, 63, 94, 0.12);
  --ge-card: rgba(28, 25, 23, 0.82);
  --ge-border: rgba(250, 250, 249, 0.08);
  --ge-text: #fafaf9;
  --ge-muted: #a8a29e;
  --ge-subtle: #78716c;
  --ge-accent: #f97316;
  --ge-accent-soft: rgba(249, 115, 22, 0.16);
  --ge-warn: #fb7185;
  --ge-chip: rgba(250, 250, 249, 0.06);
  --ge-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.7);
  color-scheme: dark;
}
* { box-sizing: border-box; }
body {
  margin: 0;
  min-height: 100vh;
  min-height: 100svh;
  display: grid;
  place-items: center;
  padding: 1.5rem 1rem;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, Tahoma, sans-serif;
  color: var(--ge-text);
  background:
    radial-gradient(60rem 40rem at 15% -10%, var(--ge-glow-a), transparent 60%),
    radial-gradient(50rem 36rem at 110% 110%, var(--ge-glow-b), transparent 60%),
    var(--ge-bg);
  -webkit-font-smoothing: antialiased;
}
.ge-card {
  width: 100%;
  max-width: 28rem;
  padding: 2.5rem 2rem 2rem;
  border: 1px solid var(--ge-border);
  border-radius: 1.5rem;
  background: var(--ge-card);
  box-shadow: var(--ge-shadow);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  text-align: center;
}
.ge-badge {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 5rem;
  height: 5rem;
  margin-bottom: 1.5rem;
  border-radius: 9999px;
  color: var(--ge-accent);
  background: var(--ge-accent-soft);
  box-shadow: 0 0 0 10px color-mix(in oklab, var(--ge-accent-soft), transparent 55%);
}
.ge-badge-alert {
  position: absolute;
  inset-inline-end: -0.125rem;
  bottom: -0.125rem;
  display: grid;
  place-items: center;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 9999px;
  color: var(--ge-warn);
  background: var(--ge-bg);
  border: 1px solid var(--ge-border);
}
.ge-title {
  margin: 0;
  font-size: 1.5rem;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.ge-desc {
  margin: 0.625rem auto 0;
  max-width: 34ch;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--ge-muted);
}
.ge-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.75rem;
}
.ge-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.75rem;
  padding: 0 1.25rem;
  border-radius: 9999px;
  font: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: transform 150ms ease, background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
}
.ge-btn:focus-visible {
  outline: 2px solid var(--ge-accent);
  outline-offset: 3px;
}
.ge-btn:active { transform: translateY(1px); }
.ge-btn-primary {
  border: 1px solid transparent;
  color: var(--ge-accent-text);
  background: var(--ge-accent);
  box-shadow: 0 8px 20px -8px var(--ge-accent);
}
.ge-btn-primary:hover { background: color-mix(in oklab, var(--ge-accent), #000 12%); }
.ge-btn-secondary {
  border: 1px solid var(--ge-border);
  color: var(--ge-text);
  background: var(--ge-chip);
}
.ge-btn-secondary:hover { border-color: color-mix(in oklab, var(--ge-text), transparent 75%); }
.ge-ref {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.75rem;
  padding: 0.25rem 0.25rem 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  color: var(--ge-subtle);
  background: var(--ge-chip);
}
[dir="rtl"] .ge-ref { padding: 0.25rem 0.75rem 0.25rem 0.25rem; }
.ge-ref code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: var(--ge-muted);
  direction: ltr;
  unicode-bidi: isolate;
}
.ge-icon-btn {
  display: inline-grid;
  place-items: center;
  width: 1.75rem;
  height: 1.75rem;
  border: 0;
  border-radius: 9999px;
  color: var(--ge-muted);
  background: transparent;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
}
.ge-icon-btn:hover { color: var(--ge-text); background: var(--ge-chip); }
.ge-icon-btn:focus-visible { outline: 2px solid var(--ge-accent); outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .ge-btn, .ge-icon-btn { transition: none; }
}
`

export default function GlobalError({
  error,
  retry,
}: {
  error: Error & { digest?: string }
  /** Re-fetches + re-renders the failed tree (Next 16.3+; was `unstable_retry`). */
  retry: () => void
}) {
  // usePathname (not `location`) so server + client agree on the locale.
  const locale = localeFromPath(usePathname())
  const [copied, setCopied] = useState(false)
  const t = COPY[locale]
  const dir = RTL_LOCALES.includes(locale) ? "rtl" : "ltr"

  useEffect(() => {
    console.error(error)
    // Central observability path (US-039) — never per-component reporting.
    reportClientError(error, "global")
  }, [error])

  // Honour the visitor's saved light/dark choice (next-themes' default
  // `theme` key); "system"/absent falls through to the OS media query.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("theme")
      if (saved === "light" || saved === "dark") {
        document.documentElement.dataset.theme = saved
      }
    } catch {
      // Storage blocked — OS preference still applies.
    }
  }, [])

  async function copyReference() {
    if (!error.digest) return
    try {
      await navigator.clipboard.writeText(error.digest)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable — the code is still visible to read out.
    }
  }

  return (
    <html lang={locale} dir={dir}>
      <head>
        <title>{t.title}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      </head>
      <body>
        <main className="ge-card" role="alert" aria-live="assertive">
          <div className="ge-badge" aria-hidden="true">
            <UtensilsCrossedIcon size={36} strokeWidth={1.75} />
            <span className="ge-badge-alert">
              <TriangleAlertIcon size={16} strokeWidth={2.25} />
            </span>
          </div>

          <h1 className="ge-title">{t.title}</h1>
          <p className="ge-desc">{t.description}</p>

          <div className="ge-actions">
            <button
              type="button"
              className="ge-btn ge-btn-primary"
              onClick={() => retry()}
            >
              <RotateCwIcon size={18} strokeWidth={2.25} aria-hidden="true" />
              {t.retry}
            </button>
            {/* Plain <a>: a full document load is the right recovery after the
                root layout itself failed — no client router state to trust. */}
            <a className="ge-btn ge-btn-secondary" href={`/${locale}`}>
              <HouseIcon size={18} strokeWidth={2.25} aria-hidden="true" />
              {t.home}
            </a>
          </div>

          {error.digest ? (
            <div className="ge-ref">
              <span>{t.reference}</span>
              <code>{error.digest}</code>
              <button
                type="button"
                className="ge-icon-btn"
                onClick={copyReference}
                aria-label={copied ? t.copied : t.copy}
                title={copied ? t.copied : t.copy}
              >
                {copied ? (
                  <CheckIcon size={14} strokeWidth={2.5} aria-hidden="true" />
                ) : (
                  <CopyIcon size={14} strokeWidth={2.25} aria-hidden="true" />
                )}
              </button>
            </div>
          ) : null}
        </main>
      </body>
    </html>
  )
}
